'use strict';

const { mapKeys, keys, get} = require('lodash');

// Utilities to convert keys beteween python delimination and javascript
// delimination.
const jsDelim = '>';
const pyDelim = '.';
const encodeKey = (key) => { return key.split(pyDelim).join(jsDelim); };
const decodeKey = (key) => { return key.split(jsDelim).join(pyDelim); };

const localeEng = require('./locales/en.json');
const {
    bot_type,
    extruders,
    mode,
    materials,
    schema_settings_types,
} = localeEng;

/**
 * A helper function that transform certain content within
 * en.json to enum constants.
 *
 * The idea is to prevent clients from having to keep their own
 * set of enums for materials, bot types, print modes, and extruders.
 */
function getConstantEnums() {
    const transformObjToEnum = objOfEntries => {
        return Object.freeze(Object.entries(objOfEntries)
            .reduce((acc, entry) => {
                // Could of used es2018 deconstruct, but we need to support
                // eagle-print...so unless we want to setup new babel rules for
                // eagle-print/desktop/node_modules/@makerbot/settingsui
                // this will have to do for now ...sigh...
                const id = entry[0];
                const name = entry[1].name;
                return Object.assign(acc, {
                    [id]: {
                        id,
                        label: name === 'High Quality' ? 'H.Q.' : name,
                    }
                });
            }, {}));
    }
    const BOT_TYPE_ENUM = transformObjToEnum(bot_type);
    const PRINT_MODES_ENUM = transformObjToEnum(mode);
    const MATERIAL_TYPES_ENUM = transformObjToEnum(materials);
    const EXTRUDERS_ENUM = transformObjToEnum(extruders);

    return {
        BOT_TYPE_ENUM,
        EXTRUDERS_ENUM,
        MATERIAL_TYPES_ENUM,
        PRINT_MODES_ENUM,
    }
}

/**
 * Wraps the logical schema returned from sliceconfig with the UI values
 * required to display it in a GUI.
 *
 * Provides translations for print modes, user settings, and user setting
 * categories defined in the external categories.json file.
 * 
 * getFullSchema() will provide the entire schema with all translations applied.
 */
function SettingsUISchema(sliceconfigSchema) {
    // grab print modes, extruders, and supported configs from the sc schema.
    // leave them unaltered.
    this.materialKeys = sliceconfigSchema.materials.supported;

    // These objects go out in API as is.
    this.extruders = sliceconfigSchema.extruders;
    this.print_modes = sliceconfigSchema.print_modes;
    this.materials = sliceconfigSchema.materials;

    // change the keys in custom parameters from their python encodings to their
    // javascript encodings, then merge them into one object. for each settings,
    // add an empty categoryIDs list to fill out later.
    this.baseSettings = mapKeys(
        sliceconfigSchema.custom_parameters,
        (value, key) => { return encodeKey(key); }
    );

    for (const key in this.baseSettings) {
        this.baseSettings[key]['categoryIDs'] = [];
    }

    // get the categories for the UI from a local json file. store them as a
    // member and add the categoryID's to each member of the baseSettings.
    this.baseCategories = require('./categories.json');
    for (const categoryID in this.baseCategories) {
        const category = this.baseCategories[categoryID];
        for (const j in category.settingIDs) {
            const settingID = category.settingIDs[j];
            if (this.baseSettings[settingID]) {
                this.baseSettings[settingID]['categoryIDs'].push(categoryID);
            }
        }
    }
}

/**
 * Returns a JSON object containing the override option's UI information,
 * which is a superset of the override option's logical information.
 * 
 * { "id" : "nested.override.id",
 *   "name" : "Nested Override",
 *   "type" : "Scalar",
 *   
 *   ... (from sliceconfig schema above)
 *   
 *   ... (appended info follows below)
 *
 *   "rationale" : "Why would we change this?"
 *   "categoryIDs" : ["someCategory", "someOtherCategory"]
 * }
 *
 * ... or null if there is no information for this override option.
 * 
 * All fields may be updated if corresponding data is 
 */
SettingsUISchema.prototype.getUserSetting = function (settingID) {

    let baseSetting = this.baseSettings[settingID];
    if (baseSetting == undefined)
        return null;

    let setting = Object.assign(
        { name: settingID, description: "", rationale: "" }, baseSetting);

    for (let key in setting) {
        if (typeof setting[key] !== 'string') continue;
        setting[key] =
            get(localeEng, `setting.${settingID}.${key}`, setting[key])
    }

    if (setting["validStrings"]) {

        let validStrings = setting["validStrings"];
        let validStringMap = {};

        for (let i = 0; i < validStrings.length; ++i) {
            validStringMap[validStrings[i]] =
                get(localeEng, `setting.${settingID}.validStrings.${validStrings[i]}`, validStrings[i]);
        }

        setting["validStrings"] = validStringMap;
    }

    /**
     * Add "input_type" to describe the type of input DOM element
     * Mainly used in CloudPrint
     */
    const type = setting.type.toLowerCase();
    switch (type) {
        case schema_settings_types.boolean:
            setting["input_type"] = 'checkbox';
            break;
        case schema_settings_types.scalar:
        case schema_settings_types.unsignedInt:
        case schema_settings_types.integer:
            setting["input_type"] = 'input';
            break;
        case schema_settings_types.string:
            setting["input_type"] = 'dropdown';
            break;
        default:
            setting["input_type"] = 'text';
    }

    return setting;
};

/**
 * Returns a JSON object containing UI information for all overridable Settings
 */
SettingsUISchema.prototype.getAllSettings = function () {
    const settingKeys = keys(this.baseSettings);
    return settingKeys.reduce(
        (acc, key) => Object.assign(acc, { [key]: this.getUserSetting(key) }),
        {}
    );
};

/**
 * Returns a JSON object containing UI information for all the Categories
 */
SettingsUISchema.prototype.getAllCategories = function () {
    /**
     * Added "categories" for the CloudPrint ExpertSettings UI component
     *   - name: The category pretty name
     *   - description: The description for the category group
     *   - content: The settings that fall under the specific category group
     *   - order: The order in displaying the category
     */
    const categoryKeys = Object.keys(localeEng['category']);
    return categoryKeys
        .reduce((acc, category, index) => (
            Object.assign(acc, {
                [category]: {
                    id: category,
                    isOpen: false,
                    name: localeEng['category'][category].name,
                    description: localeEng['category'][category].description,
                    content: this.baseCategories[category].settingIDs
                        .reduce((acc, settingId) =>
                            Object.assign(acc, { [settingId]: settingId }),
                            {}
                        ),
                    order: index += 1,
                }
            })
        ), {})
};

SettingsUISchema.prototype.getMaterialName = function (materialID) {
    let prettyName =
        get(localeEng, `materials.${materialID}.name`, materialID);

    return { "name": prettyName };
};

SettingsUISchema.prototype.getAllMaterials = function () {
    const materialKeys = this.materialKeys;
    return materialKeys.reduce(
        (acc, key) => Object.assign(acc, { [key]: this.getMaterialName(key) }),
        {}
    );
};

/**
 * Returns a JSON object with translated print modes, categories, and
 * parameters, list of supported configurations and extruders.
 */
SettingsUISchema.prototype.getFullSchema = function () {
    return {
        extruders: this.extruders,
        print_modes: this.print_modes,
        materials: this.materials,
        materials_translations: this.getAllMaterials(),
        categories: this.getAllCategories(),
        user_settings: this.getAllSettings(),
    };
};
module.exports = { SettingsUISchema, encodeKey, decodeKey, getConstantEnums };
